import React from 'react';

const friendlyTime = (timestamp) => {
  if (!timestamp) return 'just now';
  const value =
    typeof timestamp === 'object' && typeof timestamp.toMillis === 'function'
      ? timestamp.toMillis()
      : typeof timestamp === 'number'
      ? timestamp
      : Date.parse(timestamp);
  if (Number.isNaN(value)) return 'recently';
  const diffMinutes = Math.max(1, Math.floor((Date.now() - value) / 1000 / 60));
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const hours = Math.floor(diffMinutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

const getJobLink = (job) =>
  job.url ||
  job.link ||
  job.joburl ||
  job.jobUrl ||
  job.joblink ||
  job.jobLink ||
  job.applyurl ||
  job.applyUrl ||
  job.applylink ||
  job.applyLink ||
  job.apply_url ||
  job.job_url ||
  job.source_url ||
  job.sourceurl ||
  job.sourceUrl ||
  '';

const normalizeUrlCandidate = (rawLink) => {
  const value = String(rawLink || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (/^www\./i.test(value)) return `https://${value}`;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(value)) return `https://${value}`;
  return value;
};

const sanitizeJobLink = (link) => {
  if (!link || typeof link !== 'string') return '';
  try {
    const parsed = new URL(normalizeUrlCandidate(link), window.location.origin);
    const host = (parsed.hostname || '').toLowerCase();
    const currentHost = (window.location.hostname || '').toLowerCase();
    const isOwnProjectHost =
      host.includes('jobsprojectv1.web.app') ||
      host.includes('jobsprojectv1.firebaseapp.com') ||
      host.includes('jobsprojectv1');
    if (!['http:', 'https:'].includes(parsed.protocol)) return '';
    if (host === currentHost) return '';
    if (host === 'localhost' || host === '127.0.0.1') return '';
    if (isOwnProjectHost) return '';
    return parsed.href;
  } catch (error) {
    return '';
  }
};

const displayJobType = (job) => {
  const raw = (job.jobType || job.type || '').trim();
  if (!raw) return 'Full-Time';
  if (raw.toLowerCase() === 'new') return 'Full-Time';
  return raw;
};

const MetaIcon = ({ kind }) => {
  if (kind === 'company') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 21h18" />
        <path d="M5 21V7l7-3 7 3v14" />
        <path d="M9 10h.01M12 10h.01M15 10h.01M9 14h.01M12 14h.01M15 14h.01" />
      </svg>
    );
  }
  if (kind === 'location') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    );
  }
  if (kind === 'type') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="7" width="18" height="12" rx="2" />
        <path d="M9 7V5a3 3 0 0 1 6 0v2" />
      </svg>
    );
  }
  if (kind === 'experience') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 12a3 3 0 1 0-3-3 3 3 0 0 0 3 3Z" />
        <path d="M5 19a7 7 0 0 1 14 0" />
        <path d="M18 5h3" />
        <path d="M19.5 3.5v3" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v5l3 2" />
    </svg>
  );
};

const LatestJobsList = ({
  jobs = [],
  isLoading = false,
  emptyMessage = 'No jobs found.',
  onOpenJob,
}) => (
  <div className="latest-jobs">
    {isLoading
      ? Array.from({ length: 4 }).map((_, idx) => (
          <div className="job-row job-row-skeleton" key={`placeholder-${idx}`}>
            <div className="job-details">
              <div className="skeleton-line skeleton-title" />
              <div className="skeleton-line skeleton-meta" />
            </div>
            <div className="skeleton-btn" />
          </div>
        ))
      : jobs.length
      ? jobs.map((job) => {
          const jobLink = sanitizeJobLink(getJobLink(job));
          const detailPath = String(job?.detailPath || '').trim();
          const isCardClickable = Boolean(detailPath && typeof onOpenJob === 'function');
          const openDetail = () => {
            if (!isCardClickable) return;
            onOpenJob(job);
          };
          return (
            <div
              className={`job-row ${isCardClickable ? 'job-row-clickable' : ''}`}
              key={job.id || job.docId || job.title || job.jobtitle}
              onClick={openDetail}
              onKeyDown={(event) => {
                if (!isCardClickable) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openDetail();
                }
              }}
              role={isCardClickable ? 'button' : undefined}
              tabIndex={isCardClickable ? 0 : undefined}
              aria-label={isCardClickable ? `Open details for ${job.title || job.jobtitle || 'job'}` : undefined}
            >
              <div className="job-details">
                {isCardClickable ? (
                  <button type="button" className="job-title job-title-link" onClick={(event) => {
                    event.stopPropagation();
                    openDetail();
                  }}>
                    {job.title || job.jobtitle || 'Job Title'}
                  </button>
                ) : (
                  <p className="job-title">{job.title || job.jobtitle || 'Job Title'}</p>
                )}
                <div className="job-meta">
                  <span className="job-meta-item job-meta-time">
                    <span className="job-meta-icon" aria-hidden="true">
                      <MetaIcon kind="company" />
                    </span>
                    {job.company || job.employer || 'Company'}
                  </span>
                  <span className="job-meta-item job-meta-time">
                    <span className="job-meta-icon" aria-hidden="true">
                      <MetaIcon kind="location" />
                    </span>
                    {job.location || 'Location'}
                  </span>
                  <span className="job-meta-item job-meta-time">
                    <span className="job-meta-icon" aria-hidden="true">
                      <MetaIcon kind="type" />
                    </span>
                    {displayJobType(job)} · {job.workMode || job.mode || 'On-Site'}
                  </span>
                  {job.experienceDisplay ? (
                    <span className="job-meta-item job-meta-time job-meta-experience">
                      <span className="job-meta-icon" aria-hidden="true">
                        <MetaIcon kind="experience" />
                      </span>
                      {job.experienceDisplay}
                    </span>
                  ) : null}
                  <span className="job-meta-item job-meta-time">
                    <span className="job-meta-icon" aria-hidden="true">
                      <MetaIcon kind="time" />
                    </span>
                    {friendlyTime(job.postedtime || job.collecttime)}
                  </span>
                </div>
              </div>
              {jobLink ? (
                <a
                  className="apply-btn"
                  href={jobLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  Apply Now
                </a>
              ) : (
                <button className="apply-btn" type="button" disabled onClick={(event) => event.stopPropagation()}>
                  No Link
                </button>
              )}
            </div>
          );
        })
      : (
          <div className="no-results">{emptyMessage}</div>
        )}
  </div>
);

export default LatestJobsList;
